(() => {
  if (window.ToolboxCartPurchaseValidatorInitialized) return;
  window.ToolboxCartPurchaseValidatorInitialized = true;

  const config = window.ToolboxCartPurchaseValidationConfig || {};
  const storageKey = 'toolbox_post_login_cart_purchase_result';

  if (!config.customerId) return;

  function normalizeCustomerType(value) {
    const normalized = String(value || '').trim().toUpperCase();
    return normalized === 'PF' || normalized === 'PJ' ? normalized : '';
  }

  function normalizeId(value) {
    if (value === null || value === undefined || value === '') return '';
    return String(value);
  }

  function normalizeMoodle(value) {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'string') return value.trim();

    try {
      return JSON.stringify(value);
    } catch (_error) {
      return String(value).trim();
    }
  }

  function readPendingResult() {
    try {
      const rawResult = window.sessionStorage.getItem(storageKey);
      if (!rawResult) return null;
      window.sessionStorage.removeItem(storageKey);
      return JSON.parse(rawResult);
    } catch (error) {
      console.warn('[cart-purchase-validator] Não foi possível recuperar o resultado anterior.', error);
      return null;
    }
  }

  function persistResult(result) {
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(result));
    } catch (error) {
      console.warn('[cart-purchase-validator] Não foi possível persistir o resultado.', error);
    }
  }

  function complete(result) {
    if (typeof window.ToolboxCompleteCartPurchaseValidation === 'function') {
      window.ToolboxCompleteCartPurchaseValidation(result);
      return;
    }

    window.ToolboxLastCartPurchaseValidationResult = result;
    window.ToolboxCartPurchaseValidationPromise = Promise.resolve(result);
    document.dispatchEvent(
      new CustomEvent('toolbox:cart-purchase-validation:complete', { detail: result })
    );
  }

  function getItemSummary(item, reason) {
    return {
      key: String(item.key || ''),
      productId: normalizeId(item.productId),
      title: String(item.title || ''),
      url: String(item.url || ''),
      imageUrl: String(item.imageUrl || ''),
      quantity: Number(item.quantity || 0),
      moodle: normalizeMoodle(item.moodle),
      reason,
    };
  }

  function buildValidationChanges(customerType, items, purchasedItems) {
    const updates = {};
    const removedItems = [];
    const adjustedItems = [];
    const cartIdentities = new Set();
    const purchasedIdentities = new Set(
      purchasedItems
        .map((item) => customerType === 'PF'
          ? normalizeMoodle(item.moodle)
          : normalizeId(item.productId))
        .filter(Boolean)
    );

    items.forEach((item) => {
      const key = String(item.key || '');
      if (!key) return;

      const identity = customerType === 'PF'
        ? normalizeMoodle(item.moodle)
        : normalizeId(item.productId);

      if (customerType === 'PF' && !identity) {
        updates[key] = 0;
        removedItems.push(getItemSummary(item, 'missing_moodle'));
        return;
      }

      if (purchasedIdentities.has(identity)) {
        updates[key] = 0;
        removedItems.push(getItemSummary(item, 'already_purchased'));
        return;
      }

      if (cartIdentities.has(identity)) {
        updates[key] = 0;
        removedItems.push(getItemSummary(item, 'duplicate_in_cart'));
        return;
      }

      cartIdentities.add(identity);

      if (Number(item.quantity || 0) > 1) {
        updates[key] = 1;
        adjustedItems.push({
          ...getItemSummary(item, 'quantity_reduced'),
          previousQuantity: Number(item.quantity || 0),
          quantity: 1,
        });
      }
    });

    return { updates, removedItems, adjustedItems };
  }

  function notifyCartChange(cart, result) {
    document.dispatchEvent(
      new CustomEvent('cart:change', {
        detail: {
          cart,
          removedItems: result.removedItems,
          adjustedItems: result.adjustedItems,
          source: 'post-login-cart-purchase-validator',
        },
      })
    );

    document.dispatchEvent(
      new CustomEvent('cart:purchase-validation:items-updated', {
        detail: {
          cart,
          removedItems: result.removedItems,
          adjustedItems: result.adjustedItems,
        },
      })
    );

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'post-login-cart-purchase-validator' });
    }
  }

  async function waitForContextValidation() {
    if (!window.ToolboxCartContextValidationPromise) return true;

    const result = await window.ToolboxCartContextValidationPromise;
    return Boolean(result && result.status !== 'error' && result.reloadRequired !== true);
  }

  async function validatePreviousPurchases() {
    const contextIsValid = await waitForContextValidation();

    if (!contextIsValid) {
      complete({
        status: 'deferred',
        code: 'waiting_for_context_reload',
        removedItems: [],
        adjustedItems: [],
        reloadRequired: true,
      });
      return;
    }

    const customerType = normalizeCustomerType(config.customerType);
    const items = Array.isArray(config.items) ? config.items : [];
    const purchasedItems = Array.isArray(config.purchasedItems) ? config.purchasedItems : [];
    const pendingResult = readPendingResult();
    const contextResult = window.ToolboxLastCartContextValidationResult || {};

    if (!config.customerId || !customerType) {
      complete({
        status: 'error',
        code: !config.customerId ? 'customer_not_authenticated' : 'customer_type_missing',
        message: 'Não foi possível identificar o cliente ou seu tipo de pessoa.',
        removedItems: [],
        adjustedItems: [],
        reloadRequired: false,
      });
      return;
    }

    const changes = buildValidationChanges(customerType, items, purchasedItems);
    const shouldUpdateCart = Object.keys(changes.updates).length > 0;
    const contextRemovedItems = Array.isArray(contextResult.removedItems)
      ? contextResult.removedItems
      : [];

    if (!shouldUpdateCart) {
      complete({
        status: 'validated',
        code: 'previous_purchases_valid',
        customerType,
        removedItems: pendingResult && Array.isArray(pendingResult.removedItems)
          ? pendingResult.removedItems
          : [],
        adjustedItems: pendingResult && Array.isArray(pendingResult.adjustedItems)
          ? pendingResult.adjustedItems
          : [],
        contextRemovedItems: pendingResult && Array.isArray(pendingResult.contextRemovedItems)
          ? pendingResult.contextRemovedItems
          : contextRemovedItems,
        hadChanges: Boolean(pendingResult && pendingResult.hadChanges),
        cart: {
          item_count: Number(config.cartItemCount || 0),
        },
        reloadRequired: false,
      });
      return;
    }

    try {
      const response = await fetch(config.cartUpdateUrl || '/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ updates: changes.updates }),
      });
      const cart = await response.json();

      if (!response.ok) {
        throw new Error((cart && (cart.description || cart.message)) || `Cart update failed (${response.status})`);
      }

      const persistedResult = {
        status: 'validated',
        code: 'previous_purchase_items_updated',
        customerType,
        removedItems: changes.removedItems,
        adjustedItems: changes.adjustedItems,
        contextRemovedItems,
        hadChanges: true,
        cart: {
          item_count: Number(cart.item_count || 0),
        },
      };

      persistResult(persistedResult);
      notifyCartChange(cart, persistedResult);

      complete({
        ...persistedResult,
        status: 'updated',
        cart,
        reloadRequired: true,
      });

      window.setTimeout(() => window.location.reload(), 0);
    } catch (error) {
      console.error('[cart-purchase-validator] Falha ao validar compras anteriores.', error);
      complete({
        status: 'error',
        code: 'previous_purchase_update_failed',
        message: error && error.message ? error.message : 'Não foi possível atualizar o carrinho.',
        customerType,
        removedItems: [],
        adjustedItems: [],
        contextRemovedItems,
        reloadRequired: false,
      });
    }
  }

  validatePreviousPurchases();
})();
